<template>
    <div
        :id="targetId"
        :class="[
            'requirement-summary-card',
            { 'requirement-summary-card--explainable': explainable },
        ]"
        :role="explainable ? 'button' : null"
        :tabindex="explainable ? 0 : null"
        :aria-label="explainable ? `${count} ${label}. ${explanationTitle}` : null"
    >
        <div :class="['requirement-summary-card__icon', iconClass]">
            <feather-icon :icon="icon" size="24" />
        </div>
        <div>
            <strong>{{ count }}</strong>
            <span>{{ label }}</span>
        </div>
        <feather-icon
            v-if="explainable"
            icon="InfoIcon"
            size="16"
            class="requirement-summary-card__explanation-icon"
        />

        <b-popover
            v-if="explainable"
            :target="targetId"
            triggers="hover click blur"
            placement="bottom"
            custom-class="requirement-summary-card__popover"
        >
            <template #title>{{ explanationTitle }}</template>
            <div class="requirement-summary-card__groups">
                <div
                    v-for="group in explanations"
                    :key="group.id"
                    class="requirement-summary-card__group"
                >
                    <strong class="requirement-summary-card__group-title">{{ group.title }}</strong>
                    <div v-if="group.legislations.length" class="requirement-summary-card__legislations">
                        {{ group.legislations.join(', ') }}
                    </div>
                    <ul class="mb-0 pl-1">
                        <li v-for="message in group.messages" :key="message.id">
                            {{ translate(message.messageKey, message.fallbackMessage) }}
                        </li>
                    </ul>
                </div>
            </div>
        </b-popover>
    </div>
</template>

<script>
import { BPopover } from 'bootstrap-vue'

export default {
    name: 'RequirementSummaryCard',
    components: { BPopover },
    props: {
        count: { type: Number, required: true },
        label: { type: String, required: true },
        icon: { type: String, required: true },
        iconClass: { type: String, default: 'text-primary' },
        explanationTitle: { type: String, default: '' },
        explanations: { type: Array, default: () => [] },
    },
    computed: {
        explainable() {
            return this.explanations.length > 0
        },
        targetId() {
            return `requirement-summary-card-${this._uid}`
        },
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
    },
}
</script>

<style lang="scss" scoped>
.requirement-summary-card {
    align-items: center;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    display: flex;
    gap: 1rem;
    min-height: 6rem;
    padding: 1rem 1.25rem;

    &--explainable {
        cursor: help;
        outline: none;

        &:focus {
            border-color: #f58049;
        }
    }

    &__icon {
        align-items: center;
        border: 1px solid #ebe9f1;
        border-radius: 50%;
        display: flex;
        flex: 0 0 3.25rem;
        height: 3.25rem;
        justify-content: center;
    }

    strong,
    span {
        display: block;
    }

    > div > strong {
        color: #5e5873;
        font-size: 1.5rem;
        line-height: 1.2;
    }

    > div > span {
        color: #6e6b7b;
        margin-top: .25rem;
    }

    &__groups {
        max-height: 20rem;
        min-width: 18rem;
        overflow-y: auto;
    }

    &__explanation-icon {
        color: #6e6b7b;
        margin-left: auto;
    }

    &__group + &__group {
        border-top: 1px solid #ebe9f1;
        margin-top: .75rem;
        padding-top: .75rem;
    }

    &__group-title {
        color: #5e5873;
        font-size: 1rem;
    }

    &__legislations {
        color: #6e6b7b;
        font-size: .8rem;
        margin: .2rem 0 .5rem;
    }
}
</style>
